/* Console · Client mode — API client (console v1, Ale ruling 2026-11-01:
 * the demo console lives INSIDE the product, behind the login).
 *
 * Flag detection copies the deck surface's idiom: the /api/console
 * namespace 404s while `console_demo_enabled` is off (dark ship), so a
 * probe on the catalogue IS the flag read — no config round-trip. */

import { useEffect, useState } from 'react'
import { apiFetch, apiFetchBlob, ApiError } from '../../lib/api'

export interface StoryStep {
  seq: number
  filename: string
  title: string
  narration: string
  day?: number | null
  chapter?: string | null
}

export interface Story {
  id: string
  name: string
  tagline: string
  /** A variant can share an existing, server-declared signing setup. */
  setup_story_id?: string
  steps: StoryStep[]
}

export interface StoriesResponse {
  stories: Story[]
  reset_phrase: string
}

export async function fetchStories(background = false): Promise<StoriesResponse> {
  return apiFetch<StoriesResponse>('/api/console/stories', { background })
}

/** One step's PDF, served from the image — the runner feeds it into the
 *  NORMAL upload path (`POST /api/documents/upload`) as the signed-in
 *  officer, so the pipeline behaves exactly as in real use. */
export async function fetchStoryDocument(
  storyId: string,
  seq: number,
  setupEpoch: string,
  signal?: AbortSignal
): Promise<Blob> {
  return apiFetchBlob(
    `/api/console/stories/${encodeURIComponent(storyId)}/documents/${seq}?setup_epoch=${encodeURIComponent(setupEpoch)}`,
    {
      signal,
    }
  )
}

/* Once an upload POST is on the wire the server may commit it whether or
 * not the client aborts (Codex finding 2026-11-02, ConsoleHost): so story
 * uploads are never aborted — they run to settlement and are COUNTED, and
 * the reset surface stays locked while any is unresolved. The counter is
 * module-scoped on purpose: it must survive the panel and runner
 * unmounting (End story, close panel, reopen). */
let pendingUploads = 0
const pendingListeners = new Set<(n: number) => void>()
function emitPending() {
  for (const l of pendingListeners) l(pendingUploads)
}

/** Live count of story uploads the server has not yet confirmed settled. */
export function usePendingStoryUploads(): number {
  const [n, setN] = useState(pendingUploads)
  useEffect(() => {
    pendingListeners.add(setN)
    setN(pendingUploads)
    return () => {
      pendingListeners.delete(setN)
    }
  }, [])
  return n
}

/* A rejected upload is NOT a settled upload (Codex, second/third pass):
 * when the connection dies after the POST was accepted — or the server
 * answers 5xx mid-write — the document may still commit. Such an upload
 * holds its pending slot FAIL-CLOSED until the server's own record gives
 * a terminal answer: a matching document created after this run started
 * (committed — the step is then a success), or a working documents list
 * still showing nothing ten minutes on (not committed). Timeouts and
 * failing list reads never release the slot; they keep polling. Only a
 * clean 4xx (the server answered and refused) settles immediately. */
const RECONCILE_POLL_MS = 8000
const RECONCILE_SLOW_MS = 30000
const RECONCILE_ABSENT_TERMINAL_MS = 600000
const CLOCK_SKEW_MS = 120000

async function reconcileUpload(filename: string, startedAt: number): Promise<boolean> {
  for (let attempt = 0; ; attempt += 1) {
    await new Promise((r) => setTimeout(r, attempt < 6 ? RECONCILE_POLL_MS : RECONCILE_SLOW_MS))
    try {
      // Contract: the list serves `filename` (from original_filename) and
      // `upload_date` (from created_at) — see documents/router.py's
      // DocumentListItem mapping.
      const docs = await apiFetch<
        { filename?: string; original_filename?: string; upload_date?: string }[]
      >('/api/documents?sort=upload_date&order=desc&page_size=50', { background: true })
      const mine = docs.some((d) => {
        if ((d.filename ?? d.original_filename) !== filename) return false
        const born = d.upload_date ? Date.parse(d.upload_date) : NaN
        return Number.isFinite(born) && born >= startedAt - CLOCK_SKEW_MS
      })
      if (mine) return true
      if (Date.now() - startedAt > RECONCILE_ABSENT_TERMINAL_MS) return false
    } catch {
      // A failing list read proves nothing: keep the slot held and poll on.
    }
  }
}

export async function uploadStoryDocument(
  blob: Blob,
  filename: string,
  setup: ReadySetup
): Promise<void> {
  if (resetsPending > 0) throw new Error('Wait for the reset to finish before feeding documents.')
  if (!isReadySetup(setup)) throw new Error('The linked client folder is not ready.')
  const fd = new FormData()
  fd.append('file', new File([blob], filename, { type: 'application/pdf' }))
  // Delivery context only. Financial attribution still comes from the document.
  fd.append('source_id', setup.source_id)
  fd.append('client_id', setup.client_id)
  fd.append('fund_folder_id', setup.fund_folder_id)
  const startedAt = Date.now()
  pendingUploads += 1
  emitPending()
  try {
    // Deliberately NO abort signal: see the block comment above. The
    // idempotency key makes a retry safe on installs whose intake path
    // dedupes deliveries.
    await apiFetch('/api/documents/upload', {
      method: 'POST',
      body: fd,
      headers: { 'Idempotency-Key': `console-${crypto.randomUUID()}` },
    })
  } catch (e) {
    const answeredNo = e instanceof ApiError && e.status >= 400 && e.status < 500
    if (!answeredNo && (await reconcileUpload(filename, startedAt))) {
      return // the server committed it — the step genuinely landed
    }
    throw e
  } finally {
    pendingUploads -= 1
    emitPending()
  }
}

/* Reset state is module-scoped for the same reason as the upload counter:
 * the panel can unmount mid-wipe (Start story, close), and reopening must
 * neither forget a running reset nor let a story feed documents into it.
 * It is a COUNT, not a flag (Codex, third pass): two overlapping wipes
 * must not unlock when the first settles — though resetRecord itself now
 * refuses to start a second one while any is pending. */
let resetsPending = 0
const resetListeners = new Set<(b: boolean) => void>()
function emitReset() {
  for (const l of resetListeners) l(resetsPending > 0)
}

/** True while a record wipe may still be running on the server. */
export function useResetPending(): boolean {
  const [b, setB] = useState(resetsPending > 0)
  useEffect(() => {
    resetListeners.add(setB)
    setB(resetsPending > 0)
    return () => {
      resetListeners.delete(setB)
    }
  }, [])
  return b
}

export interface ResetResponse {
  ok: boolean
  summary: string
}

/* A reset whose request dies without a clean 4xx may still be running
 * server-side (the wipe subprocess outlives the connection). The wipe
 * holds a Postgres advisory lock for its whole lifetime, and
 * GET /api/console/reset/status reads it from pg_locks — the SERVER-OWNED
 * truth (Codex, third pass) — so exclusion here releases only once the
 * server says no wipe is running, never on a timer. Two consecutive
 * idle reads are required: the subprocess takes the lock milliseconds
 * after spawn, and a status read racing that window must not release. */
const STATUS_POLL_MS = 5000
/* The router kills a wipe subprocess at 180s (_RESET_TIMEOUT_S). A wipe
 * that never showed as running within that window plus margin cannot
 * start afterwards, so only then may idle reads release without ever
 * having seen the lock — early idle reads can simply predate a slow
 * child's lock acquisition. */
const NEVER_RAN_RELEASE_MS = 200000

async function holdUntilServerIdle(release: () => void): Promise<void> {
  const startedAt = Date.now()
  let idleReads = 0
  let sawRunning = false
  for (;;) {
    await new Promise((r) => setTimeout(r, STATUS_POLL_MS))
    try {
      const s = await apiFetch<{ running: boolean }>('/api/console/reset/status', {
        background: true,
      })
      if (s.running) {
        sawRunning = true
        idleReads = 0
        continue
      }
      idleReads += 1
      const neverRanOut = !sawRunning && Date.now() - startedAt > NEVER_RAN_RELEASE_MS
      if (idleReads >= 2 && (sawRunning || neverRanOut)) {
        release()
        return
      }
    } catch {
      // Status unknown proves nothing: stay locked and poll on.
      idleReads = 0
    }
  }
}

export async function resetRecord(
  confirm: string,
  previewDigest: string | null
): Promise<ResetResponse> {
  if (pendingUploads > 0) throw new Error('Wait for the pending story action to finish.')
  if (resetsPending > 0) {
    throw new Error('A record wipe is already running — wait for it to finish.')
  }
  resetsPending += 1
  emitReset()
  const release = () => {
    resetsPending -= 1
    emitReset()
  }
  let holdForServer = false
  try {
    return await apiFetch<ResetResponse>('/api/console/reset', {
      method: 'POST',
      body: JSON.stringify({ confirm, preview_digest: previewDigest }),
    })
  } catch (e) {
    // A changed-list code is a definite pre-spawn refusal. Other 409s
    // retain the existing exclusion until the server goes idle.
    const answeredNo =
      e instanceof ApiError &&
      e.status >= 400 &&
      e.status < 500 &&
      (e.status !== 409 ||
        (e.body as { detail?: { code?: string } })?.detail?.code === 'reset_preview_changed')
    holdForServer = !answeredNo
    throw e
  } finally {
    if (holdForServer) void holdUntilServerIdle(release)
    else release()
  }
}

/** null = probing · true = console on (catalogue cached) · false = dark. */
export function useConsole(): { enabled: boolean | null; catalogue: StoriesResponse | null } {
  const [state, setState] = useState<{
    enabled: boolean | null
    catalogue: StoriesResponse | null
  }>({ enabled: null, catalogue: null })
  useEffect(() => {
    let alive = true
    fetchStories(true)
      .then((c) => {
        if (alive) setState({ enabled: true, catalogue: c })
      })
      .catch((e: unknown) => {
        // 404 = the flag is off (dark namespace); anything else also keeps
        // the entry hidden — a broken probe must never paint a dead door.
        void (e instanceof ApiError)
        if (alive) setState({ enabled: false, catalogue: null })
      })
    return () => {
      alive = false
    }
  }, [])
  return state
}

export interface SigningStatus {
  folder_delivery_available?: boolean
  state: 'not_started' | 'registering' | 'ready' | 'needs_review' | 'retry'
  message: string
  setup_epoch: string
  source_id: string | null
  client_id: string | null
  fund_folder_id: string | null
  fund_id: string | null
}
export type ReadySetup = SigningStatus & {
  state: 'ready'
  source_id: string
  client_id: string
  fund_folder_id: string
  fund_id: string
}
export function isReadySetup(value: SigningStatus): value is ReadySetup {
  return (
    value.state === 'ready' &&
    Boolean(
      value.setup_epoch &&
      value.source_id &&
      value.client_id &&
      value.fund_folder_id &&
      value.fund_id
    )
  )
}
export function fetchSigning(storyId: string, signal?: AbortSignal): Promise<SigningStatus> {
  return apiFetch(`/api/console/stories/${encodeURIComponent(storyId)}/signing`, {
    background: true,
    signal,
  })
}

/** Signing has server-side shared admission plus a reset epoch. A late
 * request cannot cross a completed reset even after the UI stops waiting.
 * Upload reconciliation remains unchanged: document uploads have no epoch.
 * Never abort or retry a signing POST automatically. */
const SIGNING_OUTCOME_WAIT_MS = 120000
export async function signStory(storyId: string, setupEpoch: string): Promise<SigningStatus> {
  if (resetsPending > 0 || pendingUploads > 0)
    throw new Error('Wait for the pending story action to finish.')
  pendingUploads += 1
  emitPending()
  const deadline = Date.now() + SIGNING_OUTCOME_WAIT_MS
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      apiFetch<SigningStatus>('/api/console/stories/' + encodeURIComponent(storyId) + '/sign', {
        method: 'POST',
        body: JSON.stringify({ setup_epoch: setupEpoch }),
      }),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('Signing outcome is not confirmed.')),
          SIGNING_OUTCOME_WAIT_MS
        )
      }),
    ])
  } catch (error) {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) throw error
    while (Date.now() < deadline) {
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(RECONCILE_POLL_MS, deadline - Date.now()))
      )
      let status: SigningStatus
      try {
        status = await fetchSigning(storyId, AbortSignal.timeout(5000))
      } catch {
        continue
      }
      if (status.setup_epoch !== setupEpoch)
        throw new Error('The demo was reset. Review signing again.')
      if (status.state !== 'registering') return status
    }
    throw new Error(
      'Signing is not confirmed. Refresh status or review the reset list before continuing.'
    )
  } finally {
    clearTimeout(timer)
    pendingUploads -= 1
    emitPending()
  }
}

export function consoleErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown })?.detail
    if (typeof detail === 'string') return detail
    if (
      detail &&
      typeof detail === 'object' &&
      'message' in detail &&
      typeof detail.message === 'string'
    )
      return detail.message
  }
  return error instanceof Error ? error.message : 'The demo action could not finish.'
}
export function transientConsoleRead(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || error.status >= 500)
}

export interface ResetPreview {
  digest: string | null
  files: { path: string; label: string; state: string }[]
  sources: { id: string; name: string; active: boolean; exists: boolean }[]
  blockers: string[]
  interrupted: boolean
  can_reset: boolean
}
export function fetchResetPreview(): Promise<ResetPreview> {
  return apiFetch('/api/console/reset/preview')
}

export type DeliveryMode = 'browser' | 'folder'
export interface FolderDeliveryStatus {
  state: 'not_sent' | 'waiting' | 'received' | 'refused'
  message: string
  setup_epoch: string
  document_id: string | null
}

/** One explicit write, then receipt reads only. Backend reset admission and
 * epoch remain authoritative after a connection loss or bounded client wait.
 * Never abort a write, automatically retry a POST, or infer success by filename. */
export async function deliverStoryToFolder(
  storyId: string,
  seq: number,
  setupEpoch: string
): Promise<void> {
  if (resetsPending > 0 || pendingUploads > 0)
    throw new Error('Wait for the pending story action to finish.')
  const path = `/api/console/stories/${encodeURIComponent(storyId)}/deliveries/${seq}`
  const deadline = Date.now() + 120000
  pendingUploads += 1
  emitPending()
  let timer: ReturnType<typeof setTimeout> | undefined
  const accepted = (value: FolderDeliveryStatus): boolean => {
    if (value.setup_epoch !== setupEpoch)
      throw new Error('The demo was reset. Review the agreement again.')
    if (value.state === 'refused') throw new Error(value.message)
    if (value.state === 'received') {
      if (!value.document_id) throw new Error('The received document could not be identified.')
      return true
    }
    return false
  }
  try {
    let first: FolderDeliveryStatus | null = null
    try {
      first = await Promise.race([
        apiFetch<FolderDeliveryStatus>(path, {
          method: 'POST',
          body: JSON.stringify({ setup_epoch: setupEpoch }),
        }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('Folder delivery is not confirmed.')), 120000)
        }),
      ])
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500)
        throw new Error(consoleErrorMessage(error))
      // A lost response is not a refusal: reconcile only this exact receipt.
    }
    if (first && accepted(first)) return
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, Math.min(2000, deadline - Date.now())))
      let value: FolderDeliveryStatus
      try {
        value = await apiFetch<FolderDeliveryStatus>(
          path + `?setup_epoch=${encodeURIComponent(setupEpoch)}`,
          { background: true, signal: AbortSignal.timeout(5000) }
        )
      } catch (error) {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500)
          throw new Error(consoleErrorMessage(error))
        continue
      }
      if (accepted(value)) return
    }
    throw new Error('Receipt is not confirmed. Check delivery again or review the reset list.')
  } finally {
    clearTimeout(timer)
    pendingUploads -= 1
    emitPending()
  }
}

export interface StoryRequests {
  state: 'available' | 'incomplete' | 'unavailable'
  fund_name: string | null
  message: string | null
  letters: { id: string; recipient: string; subject: string; recorded_at: string; body: string }[]
  more_available: boolean
}
export function fetchStoryRequests(
  storyId: string,
  signal: AbortSignal,
  background = false
): Promise<StoryRequests> {
  return apiFetch(`/api/console/stories/${encodeURIComponent(storyId)}/requests`, {
    signal,
    background,
  })
}
